import type { AuthenticationResult } from '@azure/msal-browser';

import type { ValueOf } from '@evc/web-components';

import type {
  AuthDefaultPrompt,
  AuthInitializationStrategy,
} from '../../../features/config/env.type';
import type { Guid, Timestamp } from '../../../features/types/api-objects.type';
import type { ReadonlyRole } from '../types/role.type';

/** redirect notifications params possible keys - match with platform's i18n `notifications` messages */
export const RedirectNotifications = {
  KEEP_TOID_IF_RELOAD: 'auth.keep_toid_if_reload',
  SESSION_CHANGED: 'auth.session_id_changed',
} as const;
export type RedirectNotification = ValueOf<typeof RedirectNotifications>;

/** https://learn.microsoft.com/en-us/azure/active-directory-b2c/error-codes */
export const ADB2CErrors = {
  CANCELED_OPERATION: 'AADB2C90091',
  /** https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/7115 */
  AQUIRE_TOKEN_SILENT_EXPIRED_SESSION: 'AADB2C90077',
  /** custom : user closed the captcha challenge without solving it */
  CAPTCHA_CANCELLED: 'CAPTCHA_CANCELLED',
} as const;
export type ADB2CError = ValueOf<typeof ADB2CErrors>;

/** key under which `login()` embeds replay metadata in the OAuth state object
 * so a failed redirect (e.g. CAPTCHA_CANCELLED) can be retried with the exact params (authority/loginHint...) */
export const AUTH_STATE_REPLAY_KEY = '__authReplay' as const;
export type AuthStateReplay = Partial<{
  authority: string;
  loginHint: string;
  redirectUri: string;
  prompt: string;
  redirectStartPage: string;
  // will contain the provided (user) state
  state: string;
}>;

export enum Authorities {
  SIGN_UP = 'signUp',
  SIGN_UP_SIGN_IN = 'signUpSignIn',
  RESET_PASSWORD = 'resetPassword',
  // EDIT_PROFILE = 'editProfile',
}

// TODO check mambo bundle 5
export const SSO_PROVIDERS = {
  NONE: 'None',
  MICROSOFT: 'AzureAD',
} as const;
export type SSOProvider = ValueOf<typeof SSO_PROVIDERS>;

export type IdTokenClaims = {
  /** the userId */
  sub: Guid;
  legacyUserId?: Guid | '';
  /** current organization we are loggued into */
  organizationId: Guid;
  /** found licences for current user in current organization for this application
   * - coma separated if multiples versions assigned */
  licenseIds: string;
  licenseMessage: string;
  /** coma separated */
  licenseProductFeatureFlags: string;
  /** base64 encoded byte array where each bit represents a boolean for a unique permission */
  platformPermissions: string;
  /** platform's readonly role or any custom one (coma separated if multiple) */
  platformRoles: ReadonlyRole | string;
  selectedSsoProvider: SSOProvider;
  aud: Guid;
  tid: Guid;
  nonce: Guid;
  azp: Guid;
  sessionId: Guid;
  iss: string;
  ver: string;
  exp: Timestamp;
  nbf: Timestamp;
  iat: Timestamp;
} & Record<string, unknown>;

export type AuthAuthorities = `${Authorities}`;
export type AuthPolicies = Record<AuthAuthorities, string>;
export type AuthB2CPolicies = {
  names: Record<AuthAuthorities, string>;
  authorities: Record<AuthAuthorities, string>;
  authorityDomain?: string;
};

export type AuthRedirects = {
  /** provide a `back_redirect_uri` url param for aadb2c/organization-creation flows
   * if defined, will be used by :
   * - < logo will now redirect here - defaults to Connect homepage
   * - organization creation's cancel button - defaults to redirect_uri param */
  back: string;
  /** redirect here after auth complete (login) - eg back to your app
   * needs to be registred via IaC as redirectURI in idp application */
  success: string;
  /** redirect here after logout - Expect PUBLIC uri (default connect homepage) */
  logout: string;
  /** [DO NOT OVERRIDE] auth flow may redirect user to create organization (if forceOrganization) */
  organization: string;
  /** redirect here after login failure - Expect a route path ('auth-fail') to handle the issue
   * - you may protect this route with AuthFailureGuard
   * - You may use AUTH_FAIL_ROUTE env var for this one */
  loginFailedRoute: string;
};

export type AuthConfig = {
  enabled: true | boolean;
  clientId: string;
  authorityDomain?: string;
  authorityTenantId?: string;
  policies: AuthPolicies;
  redirects: AuthRedirects;
  scopes: string[];
  b2cPolicies: AuthB2CPolicies;
  storageKeys: AuthStorageKeys;
  /** user must to be part of at least 1 organization (or will be redirected to create one after login) */
  forceOrganization: false | boolean;
  /** user must have at least 1 license (or will be redirected to homepage with toast message after login) */
  forceLicense: false | boolean;
  /**
   * Prompt user to confirm/select their organization after login, before showing app content.
   *
   * **Note:** Only meaningful when `forceOrganization: true`. Without it, a user with no
   * organization would be presented with an empty selection list. Enabling this on apps
   * that do not enforce organization membership has no practical benefit.
   */
  promptOrganizations?: false | boolean;
  /** Block app readiness with a capacity screen when no license is assigned and no floating slot remains */
  promptLicenseCapacity?: false | boolean;
  /** used by default on login auth requests */
  prompt: undefined | AuthDefaultPrompt;
  /** may allow to keep last session on reload (off by default) */
  enableSessionOnReload: false | boolean;
  /** We may attempt retrieve auth on init (forcing refresh by default) */
  initializationStrategy: 'force-refresh' | AuthInitializationStrategy;
  /** Callback that will be passed the url that MSAL will navigate to in redirect flows. Returning false in the callback will stop navigation. */
  onRedirectNavigate?: (url: string) => boolean;
  /** Boolean indicating whether to navigate to the original request URL after the auth server navigates to the redirect URL. */
  navigateToLoginRequestUrl?: true | boolean;
  /** If defined, provided to auth request to bypass recaptcha challenge (usefull for E2E - use IAC_AADB2C_CAPTCHA_BYPASS_TOKEN)
   * - env `AUTH_CAPTCHA_BYPASS_TOKEN`
   * - url `?cbt=`
   * - sessionStorage `evcnt_captcha_bypass_token`
   */
  captchaBypassToken?: undefined | string;
};

export type AuthStorageKey =
  /** used in session to enforce to stay same org when reload current tabs */
  | 'toid'
  /** temporary storage to remember if user fail auth twice avoiding loops */
  | 'acquireTokenRedirectFailed'
  /** used to emit event to sync auth across tabs */
  | 'onAuthSessionChange'
  /** dev hook : sessionStorage act as providing ?cbt= */
  | 'captchaBypassToken'
  /** dev hook : sessionStorage util when use debugTools page overrides to use valid key */
  | 'captchaSiteKey';
export type AuthStorageKeys = Record<AuthStorageKey, string>;

export type AuthenticationResultWithClaims = AuthenticationResult & {
  idTokenClaims: IdTokenClaims;
};
